'use client';

import { Badge } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';

import ShoppingCart from '../../../assets/Icons/shopping-cart';

interface FloatingCartButtonProps {
  localCartCount: number;
}

export default function FloatingCartButton({
  localCartCount,
}: FloatingCartButtonProps) {
  const specialPaths = ['/books/shop'];
  const router = useRouter();
  const screens = useBreakpoint();

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 15,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: { duration: 0.2 },
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      transition: { type: 'spring', stiffness: 400, damping: 10 },
    },
    tap: { scale: 0.95 },
  };

  const badgeVariants = {
    initial: { scale: 0.8 },
    animate: {
      scale: [0.8, 1.2, 1],
      transition: { duration: 0.3 },
    },
  };

  const isSpecialPath = specialPaths.some((path) =>
    router.pathname.includes(path),
  );

  const getBottomValue = () => {
    const baseValue = !screens.lg ? 22 : 40;
    const adjustedValue = isSpecialPath ? baseValue + 30 : baseValue;
    return `${adjustedValue}px`;
  };

  if (router.pathname === '/zai') {
    return <></>;
  }

  return (
    <AnimatePresence>
      <motion.button
        style={{
          position: 'fixed',
          bottom: getBottomValue(),
          right: !screens.lg ? '22px' : '40px',
          cursor: 'pointer',
          zIndex: 999,
        }}
        className='bg-white lg:h-14 lg:w-14 w-10 h-10  hover:bg-gray-50 rounded-full p-1.5 md:p-3 shadow-2xl flex items-center justify-center'
        initial='hidden'
        animate='visible'
        exit='exit'
        whileHover='hover'
        whileTap='tap'
        variants={containerVariants}
        key={`cart-badge-${localCartCount}`}
      >
        <motion.div
          key={localCartCount}
          initial='initial'
          animate='animate'
          variants={badgeVariants}
        >
          <Badge
            style={{ color: 'white', fontSize: '11px' }}
            count={localCartCount}
          >
            <div
              onClick={() => router.push('/cart')}
              className='translate-y-1 cursor-pointer text-gray-700 hover:text-gray-500'
            >
              <ShoppingCart width={screens.lg ? 30 : 24} />
            </div>
          </Badge>
        </motion.div>
      </motion.button>
    </AnimatePresence>
  );
}
